// mvp-returns Phase H9 — Bulk Action Toolbar
// Renders above the list table when rows are selected.

import {
  Button, Group, Modal, NumberInput, Select, Stack, Table, Text, Textarea, TextInput
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { DisabledReason, HelpButtonGroup, showNotification } from "@dehub/theme";
import { DownloadSimpleIcon } from "@phosphor-icons/react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { ProposeTemplateApi, ReturnApi } from "../../apis";
import type {
  BulkAction, IBulkActionProposeBody, IBulkActionResponse, IReturnSummary,
  RefundMethod, ResolutionType, ShippingResponsibility
} from "../../apis/return";
import { currencyFormat } from "../../utils/currency";
import { downloadBlob } from "../../utils/export-returns-csv";
import { DeliveryActionButton } from "../delivery/action-controls";
import { SelectionBar } from "../delivery/atoms";
import RenderText from "../ui/render-text";
import { RETURN_BULK_MAX, ReturnAction, returnActionStatusOk, useReturnActions } from "./return-actions";

/** Toolbar action → matrix action (POST /returns/bulk-action, RETURN_PROPOSE for all). */
const BULK_ACTION: Record<BulkAction, ReturnAction> = {
  PROPOSE       : "bulkPropose",
  REJECT        : "bulkReject",
  CANCEL        : "bulkCancel",
  ACCEPT        : "bulkAccept",
  CONFIRM_REFUND: "bulkConfirmRefund",
};

/** Confirmation / small form dialog width (DeHUB UI standard, canvas Ord_Rules «д»). */
const MODAL_W = 440;

/** @dehub/help ids: `order.returns-list.action.{id}`, reason `order.returns-list.reason.bulk`. */
const BULK_HELP_ID: Record<BulkAction, string> = {
  PROPOSE       : "bulk-propose",
  REJECT        : "bulk-reject",
  CANCEL        : "bulk-cancel",
  ACCEPT        : "bulk-accept",
  CONFIRM_REFUND: "bulk-confirm-refund",
};

type TFn = (k: string, o?: Record<string, unknown>) => string;

// Action → human label, shared by the result modal summary line.
const actionLabel = (t: TFn, a: BulkAction): string => t(`bulkAction.action.${a}`);

// Resolved-at-response-time view of a bulk run, so clearing the selection
// afterwards does not blank the failed-list RET numbers.
interface IBulkResultView {
  action: BulkAction;
  total: number;
  succeeded: number;
  failed: number;
  failedRows: { returnNumber: string; returnRequestId: string; errorCode: string; errorMessage: string }[];
}

// ── Select option constants (mirror action-bar.tsx) ──

const resolutionOptions = (t: TFn) => [
  { value: "REFUND_MONEY",        label: t("bulkAction.resolution.REFUND_MONEY") },
  { value: "REPLACEMENT",         label: t("bulkAction.resolution.REPLACEMENT") },
  { value: "NEXT_ORDER_DISCOUNT", label: t("bulkAction.resolution.NEXT_ORDER_DISCOUNT") },
  { value: "NONE",                label: t("bulkAction.resolution.NONE") },
  { value: "MIXED",               label: t("bulkAction.resolution.MIXED") },
];

const refundMethodOptions = (t: TFn) => [
  { value: "BANK_TRANSFER",     label: t("refundMethod.BANK_TRANSFER") },
  { value: "INVOICE_DEDUCTION", label: t("refundMethod.INVOICE_DEDUCTION") },
  { value: "CASH",              label: t("refundMethod.CASH") },
  { value: "CREDIT_BALANCE",    label: t("refundMethod.CREDIT_BALANCE") },
];

const shippingOptions = (t: TFn): { value: string; label: string }[] => [
  { value: "BUYER_DELIVERS",       label: t("shipping.responsibility.BUYER_DELIVERS") },
  { value: "SELLER_PICKS_UP",      label: t("shipping.responsibility.SELLER_PICKS_UP") },
  { value: "BUYER_SHIPS",          label: t("shipping.responsibility.BUYER_SHIPS") },
  { value: "SELLER_PAYS_SHIPPING", label: t("shipping.responsibility.SELLER_PAYS_SHIPPING") },
  { value: "NOT_REQUIRED",         label: t("shipping.responsibility.NOT_REQUIRED") },
];

// ── Propose form state ──

interface ProposeFormState {
  refundPercent: number;
  deductionReason: string;
  shippingResponsibility: ShippingResponsibility;
  shippingCost: number | "";
  shippingNotes: string;
  proposalNotes: string;
  resolutionType: ResolutionType;
  refundMethod: RefundMethod | null;
}

function defaultProposeState(): ProposeFormState {
  return {
    refundPercent         : 100,
    deductionReason       : "",
    shippingResponsibility: "BUYER_DELIVERS",
    shippingCost          : "",
    shippingNotes         : "",
    proposalNotes         : "",
    resolutionType        : "REFUND_MONEY",
    refundMethod          : "INVOICE_DEDUCTION",
  };
}

// ── Props ──

interface Props {
  selectedIds: string[];
  onClearSelection: () => void;
  onMutate: () => void;
  /** Sum of the selected rows' totalRequestedAmount (derived in the page). */
  selectedTotal?: number;
  /** Currency of the selected rows (e.g. "MNT"); blank when unknown. */
  currency?: string;
  /** Loaded summaries for the selected rows — map result ids → RET numbers; `status` lets the
   *  toolbar disable an action no selected row can take. */
  selectedRows?: (Pick<IReturnSummary, "returnRequestId" | "returnNumber"> & Partial<Pick<IReturnSummary, "status">>)[];
}

export function BulkActionToolbar({ selectedIds, onClearSelection, onMutate, selectedTotal, currency, selectedRows }: Props) {
  const { t } = useTranslation("returns");
  const { permissionState } = useReturnActions();
  const overLimit = selectedIds.length > RETURN_BULK_MAX;

  // Permission (bulk-action.ts: RETURN_PROPOSE), the BE cap of 100 ids, then "no selected row is in a
  // status this action accepts" (only known for the rows loaded on this page).
  const bulkState = (a: BulkAction): { allowed: boolean; reason: string | null; code: string | null } => {
    const perm = permissionState(BULK_ACTION[a]);
    if (!perm.enabled) return { allowed: false, reason: perm.reason, code: "PERM" };
    if (overLimit) return { allowed: false, reason: t("bulkAction.overLimit", { max: RETURN_BULK_MAX }), code: "OVER_LIMIT" };
    const known = (selectedRows ?? []).filter((r) => r.status);
    if (known.length > 0 && known.length === selectedIds.length
      && !known.some((r) => returnActionStatusOk(BULK_ACTION[a], r.status))) {
      return { allowed: false, reason: t("bulkAction.noneInStatus"), code: "NONE_IN_STATUS" };
    }
    return { allowed: true, reason: null, code: null };
  };
  const RESOLUTION_OPTIONS = resolutionOptions(t);
  const REFUND_METHOD_OPTIONS = refundMethodOptions(t);
  const SHIPPING_OPTIONS = shippingOptions(t);
  const [proposeOpen, proposeHandlers] = useDisclosure(false);
  const [rejectOpen, rejectHandlers]   = useDisclosure(false);
  const [cancelOpen, cancelHandlers]   = useDisclosure(false);
  const [acceptOpen, acceptHandlers]   = useDisclosure(false);
  const [confirmRefundOpen, confirmRefundHandlers] = useDisclosure(false);
  const [resultOpen, resultHandlers] = useDisclosure(false);
  const [result, setResult] = useState<IBulkResultView | null>(null);
  const [busy, setBusy] = useState(false);

  // returnRequestId → RET number for the failed list (fallback: short id).
  const numberById = useMemo(
    () => new Map((selectedRows ?? []).map((r) => [r.returnRequestId, r.returnNumber])),
    [selectedRows]
  );

  // Propose form
  const [proposeForm, setProposeForm] = useState<ProposeFormState>(defaultProposeState);
  const [rejectionReason, setRejectionReason] = useState("");
  const [cancelReason, setCancelReason]       = useState("");
  const [buyerNotes, setBuyerNotes]           = useState("");
  const [bankTransactionRef, setBankTransactionRef] = useState("");

  // Templates — fetched whenever the toolbar is visible (a selection exists) so
  // the toolbar Auto-propose dropdown and the propose modal share one cache.
  const { data: templateData } = useSWR(
    selectedIds.length > 0 ? "propose-templates.list" : null,
    () => ProposeTemplateApi.list(),
    { revalidateOnFocus: false }
  );

  const templateOptions = (templateData?.items ?? []).map((t) => ({
    value: t.id,
    label: t.name,
  }));

  // Reset form when modal closes
  useEffect(() => {
    if (!proposeOpen) {
      setProposeForm(defaultProposeState());
    }
  }, [proposeOpen]);
  useEffect(() => {
    if (!rejectOpen) setRejectionReason("");
  }, [rejectOpen]);
  useEffect(() => {
    if (!cancelOpen) setCancelReason("");
  }, [cancelOpen]);
  useEffect(() => {
    if (!acceptOpen) setBuyerNotes("");
  }, [acceptOpen]);
  useEffect(() => {
    if (!confirmRefundOpen) setBankTransactionRef("");
  }, [confirmRefundOpen]);

  const setPropose = <K extends keyof ProposeFormState>(key: K, value: ProposeFormState[K]) => {
    setProposeForm((prev) => ({ ...prev, [key]: value }));
  };

  const applyTemplate = (templateId: string) => {
    const tpl = templateData?.items?.find((t) => t.id === templateId);
    if (!tpl) return;
    setProposeForm({
      refundPercent         : tpl.refundPercent,
      deductionReason       : tpl.deductionReason ?? "",
      shippingResponsibility: tpl.shippingResponsibility as ShippingResponsibility,
      shippingCost          : tpl.shippingCost ?? "",
      shippingNotes         : tpl.shippingNotes ?? "",
      proposalNotes         : tpl.proposalNotesTemplate ?? "",
      resolutionType        : tpl.resolutionType as ResolutionType,
      refundMethod          : (tpl.refundMethod as RefundMethod) ?? null,
    });
  };

  // Toolbar Auto-propose dropdown: pre-fill the propose form from the chosen
  // template and open the bulk-propose modal for review/confirm (reuses the
  // existing propose flow rather than firing a silent mutation).
  const onPickTemplate = (templateId: string | null) => {
    if (!templateId) return;
    applyTemplate(templateId);
    proposeHandlers.open();
  };

  const notifyResults = (succeeded: number, failed: number) => {
    if (failed === 0) {
      showNotification({
        color: "green", title: t("bulkAction.toast.successTitle"),
        message: t("bulkAction.toast.allUpdated", { count: succeeded })
      });
    } else {
      showNotification({
        color: "orange", title: t("bulkAction.toast.partialTitle"),
        message: t("bulkAction.toast.partial", { succeeded, failed })
      });
    }
  };

  // Surfaces the full results[] in a modal, then refreshes + clears selection.
  // The failed rows are resolved to RET numbers BEFORE clearing, so the modal
  // stays populated after the selection (and its lookup) is wiped.
  const finishBulk = (res: IBulkActionResponse) => {
    const failedRows = res.results
      .filter((r) => !r.ok)
      .map((r) => ({
        returnNumber   : numberById.get(r.returnRequestId) ?? "-",
        returnRequestId: r.returnRequestId,
        errorCode      : r.errorCode ?? "",
        errorMessage   : r.errorMessage ?? ""
      }));
    setResult({ action: res.action, total: res.total, succeeded: res.succeeded, failed: res.failed, failedRows });
    resultHandlers.open();
    notifyResults(res.succeeded, res.failed);
    onClearSelection();
    onMutate();
  };

  const exportFailed = () => {
    if (!result || result.failedRows.length === 0) return;
    const escape = (v: unknown): string => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ["returnNumber", "returnRequestId", "errorCode", "errorMessage"];
    const lines = [
      headers.join(","),
      ...result.failedRows.map((r) =>
        [r.returnNumber, r.returnRequestId, r.errorCode, r.errorMessage].map(escape).join(","))
    ];
    // UTF-8 BOM so Excel reads Cyrillic correctly (mirrors export-returns-csv).
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    downloadBlob(blob, `bulk_failed_${new Date().toISOString().slice(0, 10)}.csv`);
  };

  // O05 — below 100% the deduction reason is required; nothing (not "-") is sent when it is empty.
  const deductionMissing = proposeForm.refundPercent < 100 && !proposeForm.deductionReason.trim();

  const handleBulkPropose = async () => {
    if (deductionMissing) return;
    const proposeBody: IBulkActionProposeBody = {
      refundPercent         : proposeForm.refundPercent,
      deductionReason       : proposeForm.refundPercent < 100 ? proposeForm.deductionReason.trim() : null,
      shippingResponsibility: proposeForm.shippingResponsibility,
      shippingCost          : proposeForm.shippingCost !== "" ? Number(proposeForm.shippingCost) : null,
      shippingNotes         : proposeForm.shippingNotes || null,
      proposalNotes         : proposeForm.proposalNotes || null,
      resolutionType        : proposeForm.resolutionType,
      refundMethod          : proposeForm.resolutionType === "REFUND_MONEY" ? proposeForm.refundMethod : null,
    };
    try {
      setBusy(true);
      const res = await ReturnApi.bulkAction({
        action: "PROPOSE",
        returnRequestIds: selectedIds,
        proposeBody,
      });
      proposeHandlers.close();
      finishBulk(res);
    } catch (err) {
      showNotification({ color: "red", title: t("toast.error"), message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const handleBulkReject = async () => {
    if (!rejectionReason.trim()) {
      showNotification({ color: "red", title: t("toast.error"), message: t("bulkAction.rejectReasonRequired") });
      return;
    }
    try {
      setBusy(true);
      const res = await ReturnApi.bulkAction({
        action: "REJECT",
        returnRequestIds: selectedIds,
        rejectBody: { rejectionReason },
      });
      rejectHandlers.close();
      finishBulk(res);
    } catch (err) {
      showNotification({ color: "red", title: t("toast.error"), message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const handleBulkCancel = async () => {
    try {
      setBusy(true);
      const res = await ReturnApi.bulkAction({
        action: "CANCEL",
        returnRequestIds: selectedIds,
        cancelBody: { reason: cancelReason || null },
      });
      cancelHandlers.close();
      finishBulk(res);
    } catch (err) {
      showNotification({ color: "red", title: t("toast.error"), message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const handleBulkAccept = async () => {
    try {
      setBusy(true);
      const res = await ReturnApi.bulkAction({
        action: "ACCEPT",
        returnRequestIds: selectedIds,
        acceptBody: { buyerNotes: buyerNotes || null },
      });
      acceptHandlers.close();
      finishBulk(res);
    } catch (err) {
      showNotification({ color: "red", title: t("toast.error"), message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  const refOk = bankTransactionRef.trim().length >= 3 && bankTransactionRef.trim().length <= 128;

  const handleBulkConfirmRefund = async () => {
    if (!refOk) {
      showNotification({ color: "red", title: t("toast.error"), message: t("bulkAction.bankRefInvalid") });
      return;
    }
    try {
      setBusy(true);
      const res = await ReturnApi.bulkAction({
        action: "CONFIRM_REFUND",
        returnRequestIds: selectedIds,
        confirmRefundBody: { bankTransactionRef: bankTransactionRef.trim() },
      });
      confirmRefundHandlers.close();
      finishBulk(res);
    } catch (err) {
      showNotification({ color: "red", title: t("toast.error"), message: (err as Error).message });
    } finally {
      setBusy(false);
    }
  };

  if (selectedIds.length === 0) return null;

  return (
    <>
      <SelectionBar
        label={
          <>
            {t("bulkAction.selectedOfMax", { count: selectedIds.length, max: RETURN_BULK_MAX })}
            {selectedTotal != null ? ` · ${t("bulkAction.totalAmount", { amount: currencyFormat(selectedTotal ?? 0) })}` : ""}
            {currency && currency !== "MNT" ? ` (${currency})` : ""}
          </>
        }
      >
        <Select
          placeholder={t("bulkAction.autoProposeTemplate")}
          data={templateOptions}
          value={null}
          onChange={onPickTemplate}
          disabled={templateOptions.length === 0 || !bulkState("PROPOSE").allowed}
          clearable
          w={220}
          aria-label={t("bulkAction.autoProposeTemplate")}
        />
        {(
          [
            ["PROPOSE", proposeHandlers.open],
            ["REJECT", rejectHandlers.open],
            ["ACCEPT", acceptHandlers.open],
            ["CONFIRM_REFUND", confirmRefundHandlers.open],
            ["CANCEL", cancelHandlers.open],
          ] as [BulkAction, () => void][]
        ).map(([a, open]) => {
          const st = bulkState(a);
          // @dehub/help: attached «?» (R2) + reason popover while unavailable (R4).
          return (
            <DisabledReason key={a} helpKey="order.returns-list.reason.bulk" reason={st.code} when={!st.allowed} position="bottom-start">
              <HelpButtonGroup helpKey={`order.returns-list.action.${BULK_HELP_ID[a]}`}>
                <DeliveryActionButton data-help-id={`returns-${BULK_HELP_ID[a]}`} state={st} variant="default" onClick={open}>
                  {t(`bulkAction.action.${a}`)}
                </DeliveryActionButton>
              </HelpButtonGroup>
            </DisabledReason>
          );
        })}
        <Button variant="subtle" color="gray" onClick={onClearSelection}>
          {t("export.clear")}
        </Button>
      </SelectionBar>

      {/* BULK PROPOSE */}
      <Modal
        opened={proposeOpen}
        onClose={proposeHandlers.close}
        title={t("bulkAction.modal.proposeTitle", { count: selectedIds.length })}
        size={MODAL_W}
      >
        <Stack gap="md">
          {templateOptions.length > 0 && (
            <Select
              label={t("bulkAction.selectTemplate")}
              placeholder={t("bulkAction.autoFillFromTemplate")}
              data={templateOptions}
              clearable
              onChange={(v) => v && applyTemplate(v)}
            />
          )}
          <NumberInput
            label={t("bulkAction.refundPercent")}
            value={proposeForm.refundPercent}
            min={0}
            max={100}
            onChange={(v) => setPropose("refundPercent", typeof v === "number" ? v : 0)}
          />
          {proposeForm.refundPercent < 100 && (
            <Textarea
              label={t("actionsDialog.propose.deductionReason")}
              placeholder={t("actionsDialog.propose.deductionPlaceholder")}
              value={proposeForm.deductionReason}
              onChange={(e) => setPropose("deductionReason", e.currentTarget.value)}
              error={deductionMissing ? t("actionsDialog.propose.deductionRequired") : undefined}
              required
            />
          )}
          <Select
            label={t("detail.tab.shipping")}
            data={SHIPPING_OPTIONS}
            value={proposeForm.shippingResponsibility}
            onChange={(v) => v && setPropose("shippingResponsibility", v as ShippingResponsibility)}
            required
          />
          <Select
            label={t("filters.resolutionLabel")}
            data={RESOLUTION_OPTIONS}
            value={proposeForm.resolutionType}
            onChange={(v) => v && setPropose("resolutionType", v as ResolutionType)}
            required
          />
          {proposeForm.resolutionType === "REFUND_MONEY" && (
            <Select
              label={t("resolutionPanel.refundChannel")}
              data={REFUND_METHOD_OPTIONS}
              value={proposeForm.refundMethod ?? ""}
              onChange={(v) => setPropose("refundMethod", v as RefundMethod)}
              required
            />
          )}
          <Textarea
            label={t("bulkAction.notesOptional")}
            value={proposeForm.proposalNotes}
            onChange={(e) => setPropose("proposalNotes", e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={proposeHandlers.close}>{t("resolutionPanel.cancel")}</Button>
            <Button onClick={handleBulkPropose} loading={busy} disabled={deductionMissing}>{t("bulkAction.send")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* BULK REJECT */}
      <Modal
        opened={rejectOpen}
        onClose={rejectHandlers.close}
        title={t("bulkAction.modal.rejectTitle", { count: selectedIds.length })}
        size={MODAL_W}
      >
        <Stack gap="md">
          <Textarea
            label={t("bulkAction.rejectReason")}
            required
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={rejectHandlers.close}>{t("bulkAction.close")}</Button>
            <Button color="red" onClick={handleBulkReject} loading={busy}>{t("bulkAction.reject")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* BULK CANCEL */}
      <Modal
        opened={cancelOpen}
        onClose={cancelHandlers.close}
        title={t("bulkAction.modal.cancelTitle", { count: selectedIds.length })}
        size={MODAL_W}
      >
        <Stack gap="md">
          <Textarea
            label={t("bulkAction.reasonOptional")}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={cancelHandlers.close}>{t("bulkAction.close")}</Button>
            <Button color="gray" onClick={handleBulkCancel} loading={busy}>{t("resolutionPanel.cancel")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* BULK ACCEPT */}
      <Modal
        opened={acceptOpen}
        onClose={acceptHandlers.close}
        title={t("bulkAction.modal.acceptTitle", { count: selectedIds.length })}
        size={MODAL_W}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {t("bulkAction.acceptHint")}
          </Text>
          <Textarea
            label={t("bulkAction.noteOptional")}
            value={buyerNotes}
            onChange={(e) => setBuyerNotes(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={acceptHandlers.close}>{t("bulkAction.close")}</Button>
            <Button color="teal" onClick={handleBulkAccept} loading={busy}>{t("bulkAction.accept")}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* BULK CONFIRM REFUND */}
      <Modal
        opened={confirmRefundOpen}
        onClose={confirmRefundHandlers.close}
        title={t("bulkAction.modal.confirmRefundTitle", { count: selectedIds.length })}
        size={MODAL_W}
      >
        <Stack gap="md">
          <TextInput
            label={t("bulkAction.bankTransactionRef")}
            value={bankTransactionRef}
            onChange={(e) => setBankTransactionRef(e.currentTarget.value)}
            required
            error={
              bankTransactionRef.length > 0 && !refOk
                ? t("bulkAction.bankRefInvalid")
                : undefined
            }
          />
          <Text size="sm" c="dimmed">
            {t("bulkAction.bankRefShared")}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={confirmRefundHandlers.close}>{t("bulkAction.close")}</Button>
            <Button color="green" onClick={handleBulkConfirmRefund} loading={busy} disabled={!refOk}>
              {t("bulkAction.accept")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* BULK RESULT */}
      <Modal
        opened={resultOpen}
        onClose={resultHandlers.close}
        title={t("bulkAction.resultTitle")}
        size={600}
      >
        {result ? (
          <Stack gap="md">
            <Text size="sm">
              <Text span fw={600}>{actionLabel(t, result.action)}</Text> · {t("bulkAction.totalLabel", { count: result.total })}:{" "}
              <Text span c="green" fw={600}>{t("bulkAction.succeededLabel", { count: result.succeeded })}</Text>{" · "}
              <Text span c={result.failed > 0 ? "red" : "dimmed"} fw={600}>{t("bulkAction.failedLabel", { count: result.failed })}</Text>
            </Text>

            {result.failed > 0 ? (
              <>
                <Group justify="space-between" align="center">
                  <Text size="sm" fw={600}>{t("bulkAction.failedList")}</Text>
                  <Button
                    size="sm"
                    variant="light"
                    leftSection={<DownloadSimpleIcon size={16} />}
                    onClick={exportFailed}
                  >
                    {t("bulkAction.downloadFailed")}
                  </Button>
                </Group>
                <Table verticalSpacing="xs" horizontalSpacing="sm" withTableBorder highlightOnHover>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t("listTable.col.retNumber")}</Table.Th>
                      <Table.Th>{t("bulkAction.col.code")}</Table.Th>
                      <Table.Th>{t("bulkAction.col.error")}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {result.failedRows.map((r) => (
                      <Table.Tr key={r.returnRequestId}>
                        <Table.Td><RenderText text={r.returnNumber} size="sm" fontWeight={500} /></Table.Td>
                        <Table.Td><RenderText text={r.errorCode || "-"} size="sm" color="var(--nw-text-2)" /></Table.Td>
                        <Table.Td><RenderText text={r.errorMessage || "-"} size="sm" /></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            ) : (
              <Text size="sm" c="dimmed">{t("bulkAction.allProcessed")}</Text>
            )}

            <Text size="sm" c="dimmed">
              {t("bulkAction.sequentialHint")}
            </Text>

            <Group justify="flex-end">
              <Button onClick={resultHandlers.close}>{t("bulkAction.close")}</Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </>
  );
}
